/**
 * Get authentication headers for API requests
 * @param {boolean} includeCors - Whether to include CORS headers (default: false)
 * @returns {Object} Object containing headers for API requests
 */
export const getAuthHeaders = (includeCors = false) => {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'));
    const authToken = JSON.parse(localStorage.getItem('authToken'));
    
    // Basic headers that are always included
    const headers = {
      email: authUser?.email || '',
      auth: authToken || '',
      access: authToken || '', // For backward compatibility with existing code
    };
    
    // Add CORS headers if requested
    if (includeCors) {
      headers['Access-Control-Allow-Origin'] = '*';
      headers['Access-Control-Allow-Credentials'] = true;
      headers['Access-Control-Allow-Methods'] = 'GET, POST, PUT, DELETE, OPTIONS';
      headers['Access-Control-Allow-Headers'] = 'Origin, Content-Type, Accept, Authorization, X-Request-With';
    }
    
    return headers;
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {};
  }
};

/**
 * Get full axios request config with auth headers
 * @param {Object} options - Additional axios config options
 * @param {boolean} includeCors - Whether to include CORS headers
 * @returns {Object} Complete axios request config
 */
export const getRequestConfig = (options = {}, includeCors = false) => {
  return {
    ...options,
    headers: {
      ...getAuthHeaders(includeCors),
      ...(options.headers || {})
    }
  };
};

/**
 * Check if the user is authenticated
 * @returns {boolean} Whether the user is authenticated
 */
export const isAuthenticated = () => {
  const authUser = localStorage.getItem('authUser');
  const authToken = localStorage.getItem('authToken');
  return !!authUser && !!authToken;
};

/**
 * Check if the user has write access to a specific language
 * @param {string} language - The language to check access for
 * @returns {boolean} Whether the user has write access
 */
export const hasWriteAccess = (language) => {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'));
    if (!authUser || !authUser.languageAccess) return false;
    
    const accessLevel = authUser.languageAccess[language];
    return accessLevel === 'WRITE' || accessLevel === 'ADMIN';
  } catch (error) {
    console.error('Error checking write access:', error);
    return false;
  }
};

/**
 * Check if the user has admin access
 * @returns {boolean} Whether the user has admin access
 */
export const isAdmin = () => {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'));
    return authUser && authUser.userType === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};